// ============================================================
// CLUBHUB – Spenden: Formatierung und Bezeichnungen
// ============================================================
// Browsertauglich. Betraege stehen in den Ansichtstypen in EURO;
// `formatCurrency` rechnet in Cent, `formatEuro` (lib/utils) in Euro —
// `euro()` reicht an `formatEuro` weiter.
// ============================================================
import type { DonationKind } from "@/generated/prisma/enums";
import { formatDate, formatEuro } from "@/lib/utils";
import type { Ton } from "@/lib/labels";
import type { AnspruchsGrundlage, AufrufPhase, Belegform, Herkunft, Wertermittlung } from "./views";

/** 1234.5 → „1.234,50 €“ — die gemeinsame Fassung aus `lib/utils`. */
export function euro(betrag: number): string {
  return formatEuro(betrag);
}

/** Euro → Cent fuer `AmountInput`. */
export function cent(betrag: number | null | undefined): number | null {
  return betrag === null || betrag === undefined ? null : Math.round(betrag * 100);
}

// ── Kalender ─────────────────────────────────────────────────

const BERLIN = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" });

/** Heute in Muenchen als ISO-Datum („2026-10-23“). */
export function heuteIso(): string {
  return BERLIN.format(new Date());
}

/** Der Kalendertag eines Zeitpunkts in Berlin („2026-10-24T22:30:00Z“ → „2026-10-25“). Reine Tage bleiben, wie sie sind. */
export function berlinTag(zeitpunkt: string | Date): string {
  if (typeof zeitpunkt === "string" && /^\d{4}-\d{2}-\d{2}$/.test(zeitpunkt)) return zeitpunkt;
  const d = typeof zeitpunkt === "string" ? new Date(zeitpunkt) : zeitpunkt;
  return Number.isNaN(d.getTime()) ? String(zeitpunkt).slice(0, 10) : BERLIN.format(d);
}

export function jahrVon(iso: string): number {
  return Number(iso.slice(0, 4));
}

/** Tage zwischen zwei ISO-Daten (b − a). */
export function tageZwischen(a: string, b: string): number {
  const ta = Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10));
  const tb = Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10));
  return Math.round((tb - ta) / 86_400_000);
}

export function plusTage(iso: string, tage: number): string {
  const d = new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10) + tage));
  return d.toISOString().slice(0, 10);
}

export function plusJahre(iso: string, jahre: number): string {
  return `${jahrVon(iso) + jahre}${iso.slice(4, 10)}`;
}

/** „01.01.2025 bis 31.12.2025“ — oder ein Tag. */
export function zeitraumText(von: string | null, bis: string | null): string {
  if (!von && !bis) return "ohne Zeitraum";
  if (von && !bis) return `ab ${formatDate(von)}`;
  if (!von && bis) return `bis ${formatDate(bis)}`;
  if (von === bis) return formatDate(von);
  return `${formatDate(von)} bis ${formatDate(bis)}`;
}

// ── Betrag in Buchstaben (amtliches Muster) ──────────────────

const EINER = ["", "eins", "zwei", "drei", "vier", "fünf", "sechs", "sieben", "acht", "neun", "zehn", "elf", "zwölf", "dreizehn", "vierzehn", "fünfzehn", "sechzehn", "siebzehn", "achtzehn", "neunzehn"];
const ZEHNER = ["", "", "zwanzig", "dreißig", "vierzig", "fünfzig", "sechzig", "siebzig", "achtzig", "neunzig"];

/** 0 bis 999 — `amEnde`: ob die Zahl das Wort beschliesst („eins“) oder etwas folgt („ein…“). */
function unterTausend(n: number, amEnde: boolean): string {
  const h = Math.floor(n / 100);
  const r = n % 100;
  let text = h > 0 ? `${h === 1 ? "ein" : EINER[h]}hundert` : "";
  if (r === 0) return text;
  if (r < 20) {
    text += r === 1 && !amEnde ? "ein" : EINER[r];
  } else {
    const e = r % 10;
    const z = Math.floor(r / 10);
    text += e > 0 ? `${e === 1 ? "ein" : EINER[e]}und${ZEHNER[z]}` : ZEHNER[z];
  }
  return text;
}

/** Ganze Zahl in Worten, deutsch, klein geschrieben: 1250 → „eintausendzweihundertfünfzig“. */
export function zahlInWorten(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "";
  if (n === 0) return "null";
  const millionen = Math.floor(n / 1_000_000);
  const tausend = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const teile: string[] = [];
  if (millionen > 0) teile.push(millionen === 1 ? "eine Million" : `${unterTausend(millionen, false)} Millionen`);
  const klein = `${tausend > 0 ? `${tausend === 1 ? "ein" : unterTausend(tausend, false)}tausend` : ""}${rest > 0 ? unterTausend(rest, true) : ""}`;
  if (klein) teile.push(klein);
  return teile.join(" ");
}

/** 312.4 → „dreihundertzwölf Euro und vierzig Cent“ — so steht der Betrag auf der Bestätigung. */
export function betragInWorten(betrag: number): string {
  const cents = Math.round(betrag * 100);
  const euros = Math.floor(cents / 100);
  const rest = cents % 100;
  const euroText = euros === 1 ? "ein Euro" : `${zahlInWorten(euros)} Euro`;
  if (rest === 0) return euroText;
  return `${euroText} und ${rest === 1 ? "ein" : zahlInWorten(rest)} Cent`;
}

// ── Bezeichnungen ────────────────────────────────────────────

/** Kurz fuer Listen und Filter — `DONATION_KIND` aus `lib/labels` ist die Langform. */
export const ART_KURZ: Record<DonationKind, string> = {
  MONEY: "Geld",
  IN_KIND: "Sachspende",
  WAIVER: "Verzicht",
};

export const ART_HINWEIS: Record<DonationKind, string> = {
  MONEY: "Überweisung, Lastschrift, bar oder PayPal",
  IN_KIND: "Ein Gegenstand, bewertet mit seinem Wert am Tag der Spende",
  WAIVER: "Jemand verzichtet auf eine Erstattung, die ihm zusteht",
};

export const ART_TON: Record<DonationKind, Ton> = {
  MONEY: "neutral",
  IN_KIND: "info",
  WAIVER: "review",
};

export const HERKUNFT: Record<Herkunft, { label: string; hinweis: string }> = {
  PRIVAT: { label: "Privatvermögen", hinweis: "Bewertet mit dem gemeinen Wert (Marktwert)" },
  BETRIEB: { label: "Betriebsvermögen", hinweis: "Bewertet mit dem Entnahmewert zuzüglich der Umsatzsteuer, die auf die Entnahme entfällt" },
  UNBEKANNT: { label: "Keine Angabe", hinweis: "Der Zuwendende hat trotz Aufforderung keine Angabe gemacht" },
};

export const WERTERMITTLUNG: Record<Wertermittlung, string> = {
  RECHNUNG: "Rechnung oder Kaufbeleg",
  GUTACHTEN: "Gutachten",
  MARKTWERT: "Marktwert vergleichbarer Angebote",
  ENTNAHMEWERT: "Entnahmewert laut Buchhaltung des Spenders",
};

export const ANSPRUCH_GRUNDLAGE: Record<AnspruchsGrundlage, string> = {
  SATZUNG: "Satzung",
  BESCHLUSS: "Vorstandsbeschluss",
  VERTRAG: "Vertrag",
};

export const PHASE: Record<AufrufPhase, { label: string; ton: Ton }> = {
  GEPLANT: { label: "Geplant", ton: "info" },
  LAUFEND: { label: "Läuft", ton: "success" },
  BEENDET: { label: "Beendet", ton: "neutral" },
};

export const BELEGFORM: Record<Belegform, { label: string; titel: string }> = {
  GELD: { label: "Geldzuwendung", titel: "Bestätigung über Geldzuwendungen" },
  SACH: { label: "Sachzuwendung", titel: "Bestätigung über Sachzuwendungen" },
  SAMMEL: { label: "Sammelbestätigung", titel: "Sammelbestätigung über Geldzuwendungen" },
};

/** „1 Spende“, „3 Spenden“ */
export function anzahlText(n: number, wort: [string, string]): string {
  return `${n.toLocaleString("de-DE")} ${n === 1 ? wort[0] : wort[1]}`;
}
